/*
  Advance payment offer: the sticky button and its popup, plus the Apply / Remove buttons
  in the cart drawer and on the cart page. The code is set on the cart with the Ajax API, so
  the reduced total shows straight away in the cart and at checkout, without a page reload.
*/
if (!customElements.get('m-prepaid-offer')) {
  class MPrepaidOffer extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('[data-prepaid-modal]');
      this.addEventListener('click', this.onClick);
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
    }

    onClick = (event) => {
      if (event.target.closest('[data-prepaid-open]')) {
        this.open();
        return;
      }
      if (event.target.closest('[data-prepaid-close]')) {
        this.close();
        return;
      }
      const copy = event.target.closest('[data-prepaid-copy]');
      if (copy) this.copyCode(copy);
    };

    open() {
      this.classList.add('is-open');
      if (this.modal) this.modal.setAttribute('aria-hidden', 'false');
      const close = this.querySelector('[data-prepaid-close]');
      if (close) close.focus({ preventScroll: true });
    }

    close() {
      this.classList.remove('is-open');
      if (this.modal) this.modal.setAttribute('aria-hidden', 'true');
    }

    copyCode(button) {
      const code = this.dataset.code;
      if (!code || !navigator.clipboard) return;
      navigator.clipboard
        .writeText(code)
        .then(() => {
          const original = button.dataset.originalText || button.textContent.trim();
          button.dataset.originalText = original;
          button.textContent = 'Copied ✓';
          clearTimeout(button._prepaidTimer);
          button._prepaidTimer = setTimeout(() => {
            button.textContent = original;
          }, 2000);
        })
        .catch(() => {});
    }
  }

  customElements.define('m-prepaid-offer', MPrepaidOffer);

  // Apply / Remove buttons live in the popup and in the cart, which is re-rendered often:
  // one delegated listener covers both.
  document.addEventListener('click', (event) => {
    const apply = event.target.closest('[data-prepaid-apply]');
    const remove = event.target.closest('[data-prepaid-remove]');
    const button = apply || remove;
    if (!button || button.disabled) return;
    event.preventDefault();
    if (apply) PrepaidDiscount.apply(button);
    else PrepaidDiscount.remove(button);
  });
}

/*
  Applies and removes the code with /cart/update.js, then asks cart.js to re-render the
  drawer or cart page (cart:refresh): no page reload. Other discount codes on the cart are kept.
*/
window.PrepaidDiscount = window.PrepaidDiscount || {
  get root() {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  },

  codeFor(button) {
    const offer = document.querySelector('m-prepaid-offer');
    return (button.dataset.prepaidCode || (offer && offer.dataset.code) || '').trim();
  },

  async currentCodes() {
    try {
      const cart = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (Array.isArray(cart.discount_codes)) return cart.discount_codes.map((d) => d.code);
    } catch (error) {
      console.error(error);
    }
    // Fallback: the codes listed in the cart summary.
    return Array.from(document.querySelectorAll('[data-deli-cart-discounts] [data-discount-code]')).map(
      (el) => el.dataset.discountCode
    );
  },

  async setCodes(codes) {
    const response = await fetch(`${this.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    if (!response.ok) throw new Error('Could not update the discount');
    return response.json();
  },

  // A /discount/CODE link stores the code in this cookie and Shopify keeps re-applying it,
  // which is what stopped the code from being removed.
  clearCookie() {
    document.cookie = 'discount_code=; Max-Age=0; path=/';
  },

  // Re-render the drawer / cart page. Without either on the page, go to the cart.
  refresh(open) {
    if (!document.querySelector('m-cart-items')) {
      window.location.href = `${this.root}cart`;
      return;
    }
    const onCartPage = window.DeliSettings && DeliSettings.templateName === 'cart';
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: open && !onCartPage } }));
  },

  same(a, b) {
    return String(a).toUpperCase() === String(b).toUpperCase();
  },

  async apply(button) {
    const code = this.codeFor(button);
    if (!code) return;
    this.busy(button, true);
    try {
      const others = (await this.currentCodes()).filter((c) => !this.same(c, code));
      const cart = await this.setCodes([...others, code]);
      const result = (cart.discount_codes || []).find((d) => this.same(d.code, code));
      if (result && result.applicable === false) {
        // Keep the cart clean: drop the code that did not apply.
        await this.setCodes(others);
        throw new Error('This discount does not apply to the items in your cart.');
      }
      const offer = button.closest('m-prepaid-offer');
      if (offer && typeof offer.close === 'function') offer.close();
      this.refresh(button.hasAttribute('data-prepaid-open-cart'));
    } catch (error) {
      console.error(error);
      this.showError(button, error.message);
      this.busy(button, false);
    }
  },

  async remove(button) {
    const code = this.codeFor(button);
    if (!code) return;
    this.busy(button, true);
    try {
      const others = (await this.currentCodes()).filter((c) => !this.same(c, code));
      this.clearCookie();
      await this.setCodes(others);
      this.refresh(false);
    } catch (error) {
      console.error(error);
      this.showError(button, error.message);
      this.busy(button, false);
    }
  },

  busy(button, busy) {
    button.disabled = busy;
    button.classList.toggle('is-loading', busy);
  },

  showError(button, message) {
    const scope = button.closest('[data-prepaid-note], .m-prepaid__panel');
    const el = scope && scope.querySelector('[data-prepaid-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  },
};
